import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Text,
  Button,
  Badge,
  Spinner,
  makeStyles,
  shorthands,
  tokens,
} from '@fluentui/react-components';
import {
  Box24Regular,
  Location24Regular,
  ArrowRight16Regular,
  VehicleCab24Regular,
} from '@fluentui/react-icons';
import { usePortalAuth } from '../contexts/PortalAuthContext';
import { usePortalDebug } from '../contexts/PortalDebugContext';
import { fetchDashboard } from '../services/portalApi';
import { PortalOrder } from '../types';
import { categoryLabelForName } from '../productLabels';
import { formatCalendarDate } from '../../lib/businessTime';

const CopyableUuid: React.FC<{ label: string; value: string }> = ({ label, value }) => {
  const [copied, setCopied] = React.useState(false);
  const copy = () => {
    navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <span
      onClick={copy}
      title="Click to copy"
      style={{ fontSize: '11px', fontFamily: 'monospace', color: copied ? '#107c10' : '#a19f9d', cursor: 'pointer' }}
    >
      {label}: {copied ? 'copied!' : value}
    </span>
  );
};

const useStyles = makeStyles({
  welcomeSection: {
    marginBottom: '24px',
  },
  welcomeTitle: {
    display: 'block',
    fontSize: '28px',
    fontWeight: '600',
    color: tokens.colorNeutralForeground1,
    marginBottom: '8px',
  },
  welcomeSubtitle: {
    display: 'block',
    fontSize: '14px',
    color: tokens.colorNeutralForeground2,
  },
  card: {
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.borderRadius('8px'),
    boxShadow: tokens.shadow4,
    ...shorthands.padding('20px'),
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '16px',
  },
  cardTitle: {
    fontSize: '16px',
    fontWeight: '600',
    color: tokens.colorNeutralForeground1,
  },
  orderCard: {
    display: 'flex',
    gap: '16px',
    ...shorthands.padding('16px'),
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke2),
    ...shorthands.borderRadius('8px'),
    marginBottom: '12px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    ':hover': {
      ...shorthands.borderColor(tokens.colorBrandStroke1),
      boxShadow: '0 2px 8px rgba(0, 120, 212, 0.15)',
    },
  },
  orderMapPreview: {
    width: '120px',
    height: '90px',
    ...shorthands.borderRadius('8px'),
    backgroundColor: tokens.colorNeutralBackground3,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    position: 'relative',
    overflow: 'hidden',
  },
  orderMapOverlay: {
    position: 'absolute',
    bottom: '8px',
    left: '8px',
    right: '8px',
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    color: '#ffffff',
    fontSize: '12px',
    fontWeight: '500',
    ...shorthands.padding('4px', '8px'),
    ...shorthands.borderRadius('4px'),
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },
  orderContent: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  orderHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  orderNumber: {
    fontWeight: '600',
    color: tokens.colorNeutralForeground1,
  },
  orderProduct: {
    fontSize: '14px',
    color: tokens.colorNeutralForeground2,
  },
  orderCategory: {
    fontSize: '12px',
    color: tokens.colorNeutralForeground3,
  },
  orderLocation: {
    fontSize: '13px',
    color: tokens.colorNeutralForeground3,
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },
  orderFooter: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: 'auto',
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    textAlign: 'center' as const,
    ...shorthands.padding('32px'),
    color: tokens.colorNeutralForeground3,
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    ...shorthands.padding('40px'),
  },
});


const Dashboard: React.FC = () => {
  const styles = useStyles();
  const navigate = useNavigate();
  const { user } = usePortalAuth();
  const { debugMode } = usePortalDebug();
  const [loading, setLoading] = useState(true);
  const [activeOrders, setActiveOrders] = useState<PortalOrder[]>([]);

  useEffect(() => {
    const loadDashboard = async () => {
      setLoading(true);
      try {
        const data = await fetchDashboard();
        setActiveOrders((data.activeOrders || []).map((o: any) => ({
          ...o,
          orderNumber: o.number,
          startDate: o.startsAt,
          endDate: o.stopsAt,
          product: { id: '', name: 'Dumpster Rental' },
          location: { address1: o.deliveryAddress || '—', city: '' },
        })));
      } catch (err) {
        console.error('Failed to load dashboard:', err);
      } finally {
        setLoading(false);
      }
    };

    loadDashboard();
  }, []);

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'in_transit':
        return <Badge appearance="filled" color="brand">In Transit</Badge>;
      case 'on_site':
        return <Badge appearance="filled" color="success">On Site</Badge>;
      case 'scheduled':
        return <Badge appearance="filled" color="warning">Scheduled</Badge>;
      case 'completed':
        return <Badge appearance="outline" color="success">Completed</Badge>;
      default:
        return <Badge appearance="outline">{status}</Badge>;
    }
  };

  const formatDate = (dateString: string) => formatCalendarDate(dateString);

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));

    if (diffHours < 1) return 'Just now';
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffHours < 48) return 'Yesterday';
    return formatDate(dateString);
  };

  if (loading) {
    return (
      <div className={styles.loading}>
        <Spinner size="large" label="Loading dashboard..." />
      </div>
    );
  }

  return (
    <div>
      {/* Welcome Section */}
      <div className={styles.welcomeSection}>
        <Text as="h1" className={styles.welcomeTitle}>
          Welcome back, {user?.name?.split(' ')[0]}!
        </Text>
        <Text className={styles.welcomeSubtitle}>
          Here's what's happening with your orders
        </Text>
        {debugMode && user && (
          <div style={{ marginTop: '8px', display: 'flex', flexDirection: 'column', gap: '2px' }}>
            <CopyableUuid label="portal_user_id" value={user.id} />
            <CopyableUuid label="customer_id" value={user.customerId} />
          </div>
        )}
      </div>

      {/* Orders */}
      <div className={styles.card}>
        <div className={styles.cardHeader}>
          <Text className={styles.cardTitle}>
            Your Orders ({activeOrders.length})
          </Text>
          {activeOrders.length > 0 && (
            <Button
              appearance="transparent"
              icon={<ArrowRight16Regular />}
              iconPosition="after"
              onClick={() => navigate('/portal/orders')}
            >
              View All
            </Button>
          )}
        </div>

        {activeOrders.length === 0 ? (
          <div className={styles.emptyState}>
            <Box24Regular style={{ fontSize: '48px', marginBottom: '16px' }} />
            <Text>No active orders</Text>
          </div>
        ) : (
          activeOrders.map((order) => (
            <div
              key={order.id}
              className={styles.orderCard}
              onClick={() => navigate(`/portal/orders/${order.id}`)}
            >
              <div className={styles.orderMapPreview}>
                <Location24Regular style={{ fontSize: '32px', color: tokens.colorNeutralForeground3 }} />
                {order.tracking?.etaMinutes && (
                  <div className={styles.orderMapOverlay}>
                    <VehicleCab24Regular style={{ fontSize: '14px' }} />
                    ETA: {order.tracking.etaMinutes}min
                  </div>
                )}
              </div>
              <div className={styles.orderContent}>
                <div className={styles.orderHeader}>
                  <Text className={styles.orderNumber}>Order #{order.orderNumber}</Text>
                  {getStatusBadge(order.status)}
                </div>
                <Text className={styles.orderProduct}>{order.product.name}</Text>
                {categoryLabelForName(order.product.name) && (
                  <Text className={styles.orderCategory}>
                    {categoryLabelForName(order.product.name)}
                  </Text>
                )}
                <Text className={styles.orderLocation}>
                  <Location24Regular style={{ fontSize: '14px' }} />
                  {[order.location.address1, order.location.city].filter(Boolean).join(', ')}
                </Text>
                <div className={styles.orderFooter}>
                  <Text style={{ fontSize: '13px', color: tokens.colorNeutralForeground3 }}>
                    {formatDate(order.startDate)} → {formatDate(order.endDate)}
                  </Text>
                  {order.tracking?.status === 'en_route' && (
                    <Button
                      appearance="primary"
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/portal/tracking/${order.id}`);
                      }}
                    >
                      Track Driver
                    </Button>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default Dashboard;
